import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ArrowLeft, Save, Loader2, Send, Plus, Trash2, Wallet, Sparkles,
  CheckCircle2, XCircle, MessageSquareWarning, Banknote, AlertTriangle, History,
} from "lucide-react";
import { toast } from "sonner";
import DynamicField from "@/components/DynamicField";
import { computeItemLocal, rendicionTotal, formatCLP, estadoInfo, ESTADOS_EDITABLES } from "@/lib/gastos";

export default function RendicionEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const isAdmin = user?.cargo === "admin";

  const [categorias, setCategorias] = useState([]);
  const [reportId, setReportId] = useState(id || null);
  const [folio, setFolio] = useState("");
  const [estado, setEstado] = useState("borrador");
  const [alertas, setAlertas] = useState([]);
  const [historial, setHistorial] = useState([]);
  const [tecnicoNombre, setTecnicoNombre] = useState("");
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  const [fechaRendicion, setFechaRendicion] = useState(new Date().toISOString().split("T")[0]);
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [items, setItems] = useState([]); // [{categoria_id, valores:{}, descripcion, _pendingImage?}]

  const scanRef = useRef(null);
  const lastSnapshotRef = useRef("");
  const stateRef = useRef(null);
  const reportIdRef = useRef(reportId);
  const inFlightSaveRef = useRef(null);
  useEffect(() => { reportIdRef.current = reportId; }, [reportId]);

  const editable = ESTADOS_EDITABLES.includes(estado);
  const info = estadoInfo(estado);

  const categoriasById = {};
  categorias.forEach((c) => { categoriasById[c.id] = c; });

  useEffect(() => {
    api.get("/api/gastos/categorias?estado=activo")
      .then((r) => setCategorias(r.data || []))
      .catch(() => {});
  }, [api]);

  const loadRendicion = (rid) => {
    api.get(`/api/gastos/rendiciones/${rid}`).then((r) => {
      const d = r.data;
      const loadedItems = (d.items || []).map((it) => ({
        categoria_id: it.categoria_id,
        valores: it.valores || {},
        descripcion: it.descripcion || "",
      }));
      setFechaRendicion(d.fecha_rendicion || new Date().toISOString().split("T")[0]);
      setTitulo(d.titulo || "");
      setDescripcion(d.descripcion || "");
      setItems(loadedItems);
      setFolio(d.folio);
      setReportId(d.id);
      setEstado(d.estado || "borrador");
      setAlertas(d.alertas || []);
      setHistorial(d.historial || []);
      setTecnicoNombre(d.tecnico_nombre || "");
      lastSnapshotRef.current = JSON.stringify({
        fecha_rendicion: d.fecha_rendicion, titulo: d.titulo || "",
        descripcion: d.descripcion || "", items: loadedItems,
      });
    }).catch(() => toast.error("Error al cargar la rendición"));
  };

  useEffect(() => { if (id) loadRendicion(id); /* eslint-disable-next-line */ }, [id]);

  const payload = () => ({
    fecha_rendicion: fechaRendicion,
    titulo,
    descripcion,
    items: items.map((it) => ({
      categoria_id: it.categoria_id,
      valores: it.valores || {},
      descripcion: it.descripcion || "",
    })),
  });

  const save = async ({ silent = false } = {}) => {
    if (!editable) return reportIdRef.current;
    if (!fechaRendicion) { if (!silent) toast.error("Ingresa la fecha de rendición"); return null; }
    if (inFlightSaveRef.current) { try { await inFlightSaveRef.current; } catch (_) {} }
    const p = (async () => {
      setSaving(true);
      try {
        let savedId = reportIdRef.current || reportId;
        if (savedId) {
          await api.put(`/api/gastos/rendiciones/${savedId}`, payload());
          if (!silent) toast.success("Rendición guardada");
        } else {
          const r = await api.post("/api/gastos/rendiciones", payload());
          savedId = r.data.id;
          reportIdRef.current = savedId;
          setReportId(savedId); setFolio(r.data.folio);
          if (!silent) toast.success(`Rendición creada: ${r.data.folio}`);
        }
        setLastSavedAt(Date.now());
        lastSnapshotRef.current = JSON.stringify({ fecha_rendicion: fechaRendicion, titulo, descripcion, items });
        return savedId;
      } catch (e) {
        if (!silent) toast.error(e.response?.data?.detail || "Error al guardar");
        return null;
      } finally { setSaving(false); }
    })();
    inFlightSaveRef.current = p;
    try { return await p; } finally { if (inFlightSaveRef.current === p) inFlightSaveRef.current = null; }
  };

  // Autosave (solo cuando la rendición es editable)
  useEffect(() => { stateRef.current = { fecha_rendicion: fechaRendicion, titulo, descripcion, items }; },
    [fechaRendicion, titulo, descripcion, items]);

  useEffect(() => {
    if (!editable || saving || working) return;
    const hasItem = items.some((it) => it.categoria_id);
    if (!reportId && !hasItem) return;
    if (!fechaRendicion) return;
    const snap = JSON.stringify({ fecha_rendicion: fechaRendicion, titulo, descripcion, items });
    if (snap === lastSnapshotRef.current) return;
    const t = setTimeout(() => { save({ silent: true }); }, 2000);
    return () => clearTimeout(t);
  }, [fechaRendicion, titulo, descripcion, items, reportId, saving, working, editable]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ítems
  const addItem = () => setItems((p) => [...p, { categoria_id: "", valores: {}, descripcion: "" }]);
  const removeItem = (idx) => setItems((p) => p.filter((_, i) => i !== idx));
  const setItemCategoria = (idx, catId) =>
    setItems((p) => p.map((it, i) => {
      if (i !== idx) return it;
      const next = { ...it, categoria_id: catId, valores: {} };
      // Si venía de un escaneo sin categoría, adjuntar la foto pendiente.
      if (it._pendingImage) {
        const cat = categorias.find((c) => c.id === catId);
        const imgField = (cat?.campos || []).find((c) => c.tipo === "imagen");
        if (imgField) {
          next.valores[imgField.key] = (imgField.config || {}).multiple ? [it._pendingImage] : it._pendingImage;
          delete next._pendingImage;
        }
      }
      return next;
    }));
  const setItemValor = (idx, key, val) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, valores: { ...it.valores, [key]: val } } : it)));
  const setItemDesc = (idx, val) =>
    setItems((p) => p.map((it, i) => (i === idx ? { ...it, descripcion: val } : it)));

  // ---- Escaneo con IA ----
  const scanReceipt = async (file) => {
    if (!file) return;
    setScanning(true);
    const t = toast.loading("Leyendo el comprobante con IA…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/api/gastos/scan", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 90000,
      });
      toast.dismiss(t);
      const { ok, image_id: imageId, extraccion: ext, detail } = r.data;
      if (!ok || !ext) {
        addScannedItem(null, imageId);
        toast.info(detail || "No se pudo leer; completa los campos a mano (la foto quedó adjunta).");
        return;
      }
      if (ext.es_comprobante === false) {
        addScannedItem(null, imageId);
        toast.warning("La imagen no parece un comprobante; revisa los datos.");
        return;
      }
      addScannedItem(ext, imageId);
      toast.success(`Listo: ${ext.comercio || "comprobante"} · ${formatCLP(ext.monto)}${ext.confianza === "baja" ? " (revisa los datos)" : ""}`);
    } catch (e) {
      toast.dismiss(t);
      toast.error(e.response?.data?.detail || "Error al escanear el comprobante");
    } finally { setScanning(false); }
  };

  const addScannedItem = (ext, imageId) => {
    const cat = ext?.categoria_sugerida
      ? categorias.find((c) => (c.nombre || "").toUpperCase() === ext.categoria_sugerida.toUpperCase())
      : null;
    const item = { categoria_id: cat?.id || "", valores: {}, descripcion: "" };
    if (cat && ext) {
      const campos = cat.campos || [];
      const montoField = campos.find((c) => c.key === cat.campo_monto && c.tipo === "monto")
        || campos.find((c) => c.tipo === "monto");
      if (montoField && ext.monto) item.valores[montoField.key] = String(Math.round(ext.monto));
      const fechaField = campos.find((c) => c.tipo === "fecha");
      if (fechaField && ext.fecha) item.valores[fechaField.key] = ext.fecha;
      const imgField = campos.find((c) => c.tipo === "imagen");
      if (imgField && imageId) {
        item.valores[imgField.key] = (imgField.config || {}).multiple ? [imageId] : imageId;
      } else if (imageId) {
        item._pendingImage = imageId;
      }
    } else if (imageId) {
      item._pendingImage = imageId;
    }
    if (ext) {
      const parts = [];
      if (ext.comercio) parts.push(ext.comercio);
      if (ext.tipo_documento) parts.push(ext.tipo_documento.toUpperCase() + (ext.folio ? ` #${ext.folio}` : ""));
      if (!ext.comercio && ext.descripcion_corta) parts.push(ext.descripcion_corta);
      item.descripcion = parts.join(" · ");
    }
    setItems((p) => [...p, item]);
  };

  const total = rendicionTotal(categoriasById, items);

  // ---- Flujo de aprobación ----
  const enviar = async () => {
    if (!fechaRendicion) { toast.error("Ingresa la fecha de rendición"); return; }
    if (!items.some((it) => it.categoria_id)) { toast.error("Agrega al menos un gasto con categoría"); return; }
    setWorking(true);
    try {
      if (inFlightSaveRef.current) { try { await inFlightSaveRef.current; } catch (_) {} }
      const savedId = reportIdRef.current || (await save({ silent: true }));
      if (!savedId) throw new Error("No se pudo guardar");
      await api.put(`/api/gastos/rendiciones/${savedId}`, payload());
      lastSnapshotRef.current = JSON.stringify({ fecha_rendicion: fechaRendicion, titulo, descripcion, items });
      const r = await api.post(`/api/gastos/rendiciones/${savedId}/enviar`);
      (r.data.alertas || []).forEach((a) => toast.warning(a, { duration: 8000 }));
      toast.success(`Rendición ${r.data.folio} enviada a aprobación`);
      navigate("/tecnico/gastos");
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Error al enviar");
    } finally { setWorking(false); }
  };

  const accionAdmin = async (accion, { conComentario = false, pregunta = "" } = {}) => {
    let comentario = "";
    if (conComentario) {
      comentario = window.prompt(pregunta) || "";
      if (!comentario.trim()) { toast.error("Debes indicar el motivo"); return; }
    }
    setWorking(true);
    try {
      const r = await api.post(`/api/gastos/rendiciones/${reportId}/${accion}`, { comentario });
      toast.success(r.data.message);
      loadRendicion(reportId);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally { setWorking(false); }
  };

  const ultimaObservacion = [...historial].reverse().find((h) => h.estado === "observada" || h.estado === "rechazada");

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <header className="bg-white border-b sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3 min-w-0">
            <Wallet className="h-5 w-5 text-[#534AB7] flex-shrink-0" />
            <span className="text-sm font-semibold text-[#384091] truncate">{folio || "Nueva rendición"}</span>
            <Badge className={`${info.badge} border-0 text-[10px] flex-shrink-0`}>{info.label}</Badge>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {editable && (
              <>
                <span className="hidden sm:inline-block text-[11px] text-gray-500 mr-1">
                  {saving ? "Guardando…" : lastSavedAt ? `Guardado ${new Date(lastSavedAt).toLocaleTimeString("es-CL", { hour: "2-digit", minute: "2-digit" })}` : "Auto-guardado activo"}
                </span>
                <Button variant="outline" size="sm" onClick={() => save()} disabled={saving || working}>
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Guardar
                </Button>
              </>
            )}
            <Button variant="ghost" size="sm" onClick={() => navigate("/tecnico/gastos")}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-4">
        {/* Observaciones de devolución / rechazo */}
        {(estado === "observada" || estado === "rechazada") && ultimaObservacion?.comentario && (
          <Card className={`border-0 shadow-sm ${estado === "observada" ? "bg-purple-50" : "bg-red-50"}`}>
            <CardContent className="p-4 flex items-start gap-3">
              <MessageSquareWarning className={`h-5 w-5 flex-shrink-0 mt-0.5 ${estado === "observada" ? "text-purple-600" : "text-red-600"}`} />
              <div>
                <p className={`text-sm font-semibold ${estado === "observada" ? "text-purple-800" : "text-red-800"}`}>
                  {estado === "observada" ? "Devuelta con observaciones" : "Rechazada"} por {ultimaObservacion.por}
                </p>
                <p className={`text-sm mt-0.5 ${estado === "observada" ? "text-purple-700" : "text-red-700"}`}>
                  {ultimaObservacion.comentario}
                </p>
                {estado === "observada" && (
                  <p className="text-xs text-purple-500 mt-1">Corrige lo indicado y vuelve a enviarla.</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Alertas de política / duplicados */}
        {alertas.length > 0 && (
          <Card className="border-0 shadow-sm bg-amber-50">
            <CardContent className="p-4">
              <p className="text-sm font-semibold text-amber-800 flex items-center gap-2 mb-1.5">
                <AlertTriangle className="h-4 w-4" /> Alertas
              </p>
              <ul className="space-y-1">
                {alertas.map((a, i) => <li key={i} className="text-xs text-amber-700">• {a}</li>)}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Cabecera */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-lg text-[#384091]">Datos de la rendición</h2>
              {isAdmin && tecnicoNombre && (
                <span className="text-xs text-gray-500">Rinde: <b>{tecnicoNombre}</b></span>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Fecha de rendición <span className="text-red-500 text-xs">*</span></Label>
                <Input type="date" value={fechaRendicion} onChange={(e) => setFechaRendicion(e.target.value)}
                  disabled={!editable} className="mt-1 h-11 bg-white no-uppercase" />
              </div>
              <div className="sm:col-span-2">
                <Label>Título / referencia</Label>
                <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} disabled={!editable}
                  placeholder="Ej: Visita técnica Rancagua" className="mt-1 h-11 bg-white" />
              </div>
              <div className="sm:col-span-3">
                <Label>Descripción</Label>
                <Textarea rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
                  disabled={!editable} placeholder="Detalle general (opcional)" className="mt-1 bg-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        {categorias.length === 0 && (
          <Card className="border-0 shadow-sm bg-amber-50">
            <CardContent className="p-4 flex items-center justify-between gap-3">
              <p className="text-sm text-amber-800">No hay categorías activas. {isAdmin ? "Configúralas primero." : "Pide al administrador que las configure."}</p>
              {isAdmin && (
                <Button size="sm" variant="outline" className="border-amber-500 text-amber-700 flex-shrink-0"
                  onClick={() => navigate("/tecnico/gastos/config")}>Configurar</Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Ítems */}
        {items.map((it, idx) => {
          const cat = categoriasById[it.categoria_id];
          const computed = cat ? computeItemLocal(cat, it.valores) : { valores: it.valores || {}, monto: 0 };
          const itemMonto = computed.monto;
          const campos = cat ? [...(cat.campos || [])].sort((a, b) => (a.orden || 0) - (b.orden || 0)) : [];
          return (
            <Card key={idx} className="border-0 shadow-sm">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-semibold text-[#0FB4B7]">Gasto {idx + 1}</h3>
                  {editable && (
                    <Button variant="ghost" size="sm" onClick={() => removeItem(idx)}
                      className="text-red-400 hover:text-red-600 h-9 w-9 p-0">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
                {it._pendingImage && !it.categoria_id && (
                  <p className="text-xs text-purple-600 bg-purple-50 rounded-lg px-3 py-2">
                    📎 Hay una foto escaneada esperando: elige la categoría y se adjuntará sola.
                  </p>
                )}
                <div>
                  <Label>Categoría <span className="text-red-500 text-xs">*</span></Label>
                  <Select value={it.categoria_id || ""} onValueChange={(v) => setItemCategoria(idx, v)} disabled={!editable}>
                    <SelectTrigger className="mt-1 h-11 bg-white"><SelectValue placeholder="Seleccionar categoría" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {cat && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    {campos.map((campo) => (
                      <div key={campo.key} className={["textarea", "imagen"].includes(campo.tipo) ? "sm:col-span-2" : ""}>
                        <DynamicField
                          campo={campo}
                          value={computed.valores[campo.key]}
                          valores={it.valores}
                          disabled={!editable}
                          onChange={(v) => setItemValor(idx, campo.key, v)}
                        />
                      </div>
                    ))}
                    <div className="sm:col-span-2">
                      <Label className="text-sm">Nota del gasto</Label>
                      <Input value={it.descripcion || ""} onChange={(e) => setItemDesc(idx, e.target.value)}
                        disabled={!editable} placeholder="Ej: SHELL RUTA 68 · BOLETA #12345"
                        className="mt-1 h-11 bg-white" />
                    </div>
                  </div>
                )}

                {cat && (
                  <div className="flex justify-end pt-2 border-t">
                    <span className="text-sm text-gray-500 mr-2">Monto del gasto:</span>
                    <span className="text-sm font-bold text-[#384091]">{formatCLP(itemMonto)}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}

        {editable && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <input ref={scanRef} type="file" accept="image/*" capture="environment" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; scanReceipt(f); }} />
            <Button variant="outline" className="h-12 border-dashed border-[#534AB7] text-[#534AB7] hover:bg-[#534AB7]/5"
              onClick={() => scanRef.current?.click()} disabled={scanning || categorias.length === 0}>
              {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
              Escanear boleta con IA
            </Button>
            <Button variant="outline" className="h-12 border-dashed" onClick={addItem}
              disabled={categorias.length === 0}>
              <Plus className="h-4 w-4 mr-2" /> Agregar gasto manual
            </Button>
          </div>
        )}

        {/* Total + acciones */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-base font-semibold text-gray-700">Total rendición</span>
              <span className="text-2xl font-bold text-[#534AB7]">{formatCLP(total)}</span>
            </div>

            {editable && (
              <Button className="w-full h-12 bg-[#384091] hover:bg-[#2D3375] text-white font-medium"
                onClick={enviar} disabled={working || saving}>
                {working ? <><Loader2 className="h-5 w-5 animate-spin mr-2" />Procesando...</>
                  : <><Send className="h-5 w-5 mr-2" />{estado === "observada" ? "Reenviar a aprobación" : "Enviar a aprobación"}</>}
              </Button>
            )}

            {isAdmin && estado === "enviada" && reportId && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <Button className="h-11 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={working}
                  onClick={() => accionAdmin("aprobar")}>
                  <CheckCircle2 className="h-4 w-4 mr-1.5" /> Aprobar
                </Button>
                <Button variant="outline" className="h-11 border-purple-400 text-purple-700 hover:bg-purple-50" disabled={working}
                  onClick={() => accionAdmin("observar", { conComentario: true, pregunta: "Observaciones para el técnico:" })}>
                  <MessageSquareWarning className="h-4 w-4 mr-1.5" /> Devolver
                </Button>
                <Button variant="outline" className="h-11 border-red-400 text-red-600 hover:bg-red-50" disabled={working}
                  onClick={() => accionAdmin("rechazar", { conComentario: true, pregunta: "Motivo del rechazo:" })}>
                  <XCircle className="h-4 w-4 mr-1.5" /> Rechazar
                </Button>
              </div>
            )}

            {isAdmin && estado === "aprobada" && reportId && (
              <Button className="w-full h-11 bg-[#0FB4B7] hover:bg-[#0D999C] text-white" disabled={working}
                onClick={() => accionAdmin("reembolsar")}>
                <Banknote className="h-4 w-4 mr-2" /> Marcar como reembolsada
              </Button>
            )}
          </CardContent>
        </Card>

        {/* Historial */}
        {historial.length > 0 && (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                <History className="h-4 w-4 text-[#534AB7]" /> Historial
              </p>
              <div className="space-y-2.5">
                {[...historial].reverse().map((h, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${i === 0 ? "bg-[#534AB7]" : "bg-gray-300"}`} />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-700">
                        <b className="capitalize">{h.accion}</b> por {h.por}
                        <span className="text-gray-400"> · {new Date(h.fecha).toLocaleString("es-CL", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                      </p>
                      {h.comentario && <p className="text-xs text-gray-500 italic">"{h.comentario}"</p>}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
}
